/**
 * 🔐 AuthContext 통합 Axios 인스턴스
 * FSD shared layer - 모든 도메인 API 클라이언트에서 사용할 공통 axios 인스턴스
 * 
 * @example
 * ```ts
 * // Orval 설정에서 사용
 * import { createAuthAxiosInstance } from '@edusense/shared-auth-core/axios';
 * 
 * export const customInstance = createAuthAxiosInstance({
 *   baseURL: 'https://api.example.com',
 * });
 * ```
 */

import axios, { AxiosError, AxiosHeaders, AxiosInstance, AxiosRequestConfig } from 'axios';
import { tokenManager } from '../token-manager';

const XSRF_COOKIE_NAME = 'XSRF-TOKEN';
const XSRF_HEADER_NAME = 'X-XSRF-TOKEN';

const isBrowserEnvironment = typeof document !== 'undefined';
const unsafeMethods = new Set(['post', 'put', 'patch', 'delete']);

const getCookieValue = (cookieName: string): string | null => {
  if (!isBrowserEnvironment) {
    return null;
  }

  const cookies = document.cookie ? document.cookie.split(';') : [];
  for (const rawCookie of cookies) {
    const [name, ...valueParts] = rawCookie.trim().split('=');
    if (name === cookieName) {
      return decodeURIComponent(valueParts.join('='));
    }
  }

  return null;
};

const shouldAttachCsrfHeader = (method?: string): boolean => {
  if (!method) {
    return false;
  }
  return unsafeMethods.has(method.toLowerCase());
};

/**
 * AuthContext 통합 Axios 인스턴스 설정
 */
export interface AuthAxiosConfig {
  baseURL: string;
  timeout?: number;
  defaultHeaders?: Record<string, string>;
  enableDebug?: boolean;
  onAuthError?: () => void;
  withCredentials?: boolean;
  authServerUrl?: string;
  oauthClientId?: string;
}

/**
 * 기본 설정
 */
const DEFAULT_CONFIG: Partial<AuthAxiosConfig> = {
  timeout: 30000,
  // Do NOT force 'Content-Type' here. Let request-level logic set it
  // so that FormData requests get proper multipart boundaries.
  defaultHeaders: {
    'Accept': 'application/json',
  },
  enableDebug: process.env.NODE_ENV === 'development',
};

/**
 * AuthContext 기반 Axios 인스턴스 생성
 * @param config Axios 인스턴스 설정
 * @returns 인증이 통합된 Axios 인스턴스
 */
export function createAuthAxiosInstance(config: AuthAxiosConfig): AxiosInstance {
  const finalConfig = { ...DEFAULT_CONFIG, ...config };
  
  // Axios 인스턴스 생성
  const instance = axios.create({
    baseURL: finalConfig.baseURL,
    timeout: finalConfig.timeout,
    headers: finalConfig.defaultHeaders,
    withCredentials: finalConfig.withCredentials,
  });

  // Request interceptor - 인증 헤더 자동 추가
  instance.interceptors.request.use(
    (requestConfig) => {
      // TokenManager에서 현재 액세스 토큰 가져오기
      const accessToken = tokenManager.getToken();

      if (accessToken) {
        if (requestConfig.headers instanceof AxiosHeaders) {
          requestConfig.headers.set('Authorization', `Bearer ${accessToken}`);
        } else {
          requestConfig.headers = requestConfig.headers ?? {};
          requestConfig.headers.Authorization = `Bearer ${accessToken}`;
        }
      }

      const needsCsrfHeader = shouldAttachCsrfHeader(requestConfig.method);
      let csrfToken: string | null = null;

      if (needsCsrfHeader) {
        csrfToken = getCookieValue(XSRF_COOKIE_NAME);
        if (csrfToken) {
          if (requestConfig.headers instanceof AxiosHeaders) {
            if (!requestConfig.headers.has(XSRF_HEADER_NAME)) {
              requestConfig.headers.set(XSRF_HEADER_NAME, csrfToken);
            }
          } else {
            requestConfig.headers = requestConfig.headers ?? {};
            if (!(XSRF_HEADER_NAME in requestConfig.headers)) {
              requestConfig.headers[XSRF_HEADER_NAME] = csrfToken;
            }
          }
        }
      }

      // 디버그 로깅
      if (finalConfig.enableDebug) {
        console.log('🌐 [API 요청]', {
          method: requestConfig.method?.toUpperCase(),
          url: requestConfig.url,
          baseURL: requestConfig.baseURL,
          fullURL: requestConfig.baseURL ? `${requestConfig.baseURL}${requestConfig.url}` : requestConfig.url,
          hasAuth: !!accessToken,
          hasCsrf: needsCsrfHeader && !!csrfToken,
          timestamp: new Date().toISOString(),
        });
      }

      return requestConfig;
    },
    (error) => {
      if (finalConfig.enableDebug) {
        console.error('❌ [API 요청 에러]', error);
      }
      return Promise.reject(error);
    }
  );

  // Response interceptor - 인증 에러 처리
  instance.interceptors.response.use(
    (response) => {
      // 성공 응답 디버그 로깅
      if (finalConfig.enableDebug) {
        console.log('✅ [API 응답]', {
          status: response.status,
          url: response.config.url,
          timestamp: new Date().toISOString(),
        });
      }
      
      return response;
    },
    async (error: AxiosError) => {
      const originalRequest = error.config as any;

      // 401 Unauthorized 처리
      if (error.response?.status === 401 && !originalRequest._retry) {
        console.warn('🔐 [인증 만료 감지] URL:', error.config?.url, 'retry flag:', originalRequest._retry);
        if (finalConfig.enableDebug) {
          console.warn('🔐 [인증 만료] 토큰 리프레시 시도');
        }

        // 재시도 플래그 설정 (무한 루프 방지)
        originalRequest._retry = true;

        // 리프레시 토큰으로 액세스 토큰 갱신 시도
        const refreshToken = tokenManager.getRefreshToken();
        console.log('🔐 [리프레시 토큰 확인] 존재:', !!refreshToken, '길이:', refreshToken?.length || 0);
        if (refreshToken) {
          try {
            console.log('🔐 [리프레시 토큰 갱신 시작] tokenType:', tokenManager.getTokenType());
            const { handleRefreshTokenFlow } = await import('../utils/refresh');
            const authServerUrl = finalConfig.authServerUrl || 'https://auth.ugot.uk';

            const result = await handleRefreshTokenFlow(
              refreshToken,
              authServerUrl,
              tokenManager.getTokenType(),
              finalConfig.oauthClientId
            );

            console.log('🔐 [리프레시 토큰 갱신 결과]', { success: result.success, hasAccessToken: !!result.accessToken, error: result.error });
            if (result.success && result.accessToken) {
              // 새 토큰 저장
              if (result.refreshToken) {
                tokenManager.setRefreshToken(result.refreshToken);
              }
              if (result.expiresIn) {
                tokenManager.setExpiresIn(result.expiresIn);
              }
              tokenManager.setToken(result.accessToken, result.tokenType || 'Bearer');

              // 원래 요청에 새 토큰 추가하여 재시도
              originalRequest.headers.Authorization = `Bearer ${result.accessToken}`;
              return instance(originalRequest);
            }
          } catch (refreshError) {
            console.error('❌ [토큰 리프레시 실패]', refreshError);
            if (finalConfig.enableDebug) {
              console.error('❌ [토큰 리프레시 실패 - 디버그]', refreshError);
            }
          }
        } else {
          console.warn('🔐 [리프레시 토큰 없음] 토큰 갱신 불가');
        }
        // 현재는 단순히 로그아웃 처리
        if (finalConfig.enableDebug) {
          console.warn('🔐 [인증 만료] 토큰 클리어 및 로그아웃 처리');
        }
        
        // 토큰 클리어
        tokenManager.clearToken();
        
        // 커스텀 인증 에러 핸들러 호출
        if (finalConfig.onAuthError) {
          finalConfig.onAuthError();
        }
      }

      // 에러 디버그 로깅
      if (finalConfig.enableDebug) {
        console.error('❌ [API 응답 에러]', {
          status: error.response?.status,
          url: error.config?.url,
          message: error.message,
          timestamp: new Date().toISOString(),
        });
      }

      return Promise.reject(error);
    }
  );

  return instance;
}

/**
 * Orval용 커스텀 인스턴스 생성 헬퍼
 * @param baseURL API 베이스 URL
 * @param additionalConfig 추가 설정
 * @returns Orval 호환 커스텀 인스턴스 함수
 */
export function createOrvalCustomInstance(
  baseURL: string,
  additionalConfig?: Partial<AuthAxiosConfig>
) {
  // baseURL이 /로 끝나도록 보장
  const normalizedBaseURL = baseURL.endsWith('/') ? baseURL : baseURL + '/';

  const instance = createAuthAxiosInstance({
    baseURL: normalizedBaseURL,
    ...additionalConfig,
  });

  // Orval이 기대하는 커스텀 인스턴스 함수 형태
  return function customInstance<T>(
    config: AxiosRequestConfig,
    options?: AxiosRequestConfig,
  ): Promise<T> {
    const source = axios.CancelToken.source();

    // URL 처리: baseURL 끝에 /가 없으면 추가하고, URL 앞의 /는 제거
    const processedConfig = { ...config };

    // baseURL이 /로 끝나지 않으면 추가
    let effectiveBaseURL = baseURL;
    if (effectiveBaseURL && !effectiveBaseURL.endsWith('/')) {
      effectiveBaseURL = effectiveBaseURL + '/';
    }

    // URL이 /로 시작하면 제거
    if (processedConfig.url && processedConfig.url.startsWith('/')) {
      processedConfig.url = processedConfig.url.substring(1);
    }

    // baseURL을 config에 명시적으로 설정
    processedConfig.baseURL = effectiveBaseURL;

    // 디버그: config와 options 확인
    console.log('🔍 Orval customInstance 호출:', {
      originalUrl: config.url,
      processedUrl: processedConfig.url,
      effectiveBaseURL,
      finalURL: `${effectiveBaseURL}${processedConfig.url}`,
      options,
    });

    const promise = instance({
      ...processedConfig,
      ...options,
      cancelToken: source.token,
    }).then(({ data }) => data);

    // @ts-ignore - Orval에서 기대하는 cancel 메서드 추가
    promise.cancel = () => {
      source.cancel('Query was cancelled');
    };

    return promise;
  };
}

/**
 * 에러 타입 (React Query/Orval 호환)
 */
export type AuthAxiosError<T = any> = AxiosError<T>;
